import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, WhatsappLogo, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const TEMPLATE_COPY = {
  itinerary_update_notifications: {
    heading: '🗓️ Wedding Itinerary Updated',
    linkAfter: 3,
    paragraphs: ({ guestName, hostNames }) => [
      `Dear ${guestName},`,
      `The itinerary for the wedding celebrations of ${hostNames} has been updated.`,
      'Please review the latest event schedule to stay informed about the updated timings and event details.',
      'You may access the updated itinerary here:',
      'We look forward to celebrating with you. Thank you 💗',
    ],
  },
  wishlist_notifications: {
    heading: '🎁 A Thoughtful Gift Starts Here',
    linkAfter: 2,
    paragraphs: ({ guestName, hostNames }) => [
      `Dear ${guestName},`,
      `As you prepare to celebrate the wedding of ${hostNames}, the couple has shared their gift wishlist to help guests plan their blessings with ease.`,
      'You can view their wishlist here:',
      'Thank you for being a cherished part of their special day. 💖',
    ],
  },
  travel_det_upload_reminder: {
    heading: '✈️ Travel Details Required',
    linkAfter: 2,
    paragraphs: ({ guestName, hostNames }) => [
      `Dear ${guestName},`,
      `To help us plan your welcome and stay for the wedding of ${hostNames}, we kindly request you to share your arrival and departure details.`,
      'Please update your travel information using the link below:',
      'Thank you. We look forward to welcoming you soon! 💐',
    ],
  },
  id_upload_reminders: {
    heading: '🪪 ID Verification Required',
    linkAfter: 3,
    paragraphs: ({ guestName, hostNames }) => [
      `Dear ${guestName},`,
      `To arrange your accommodation for the wedding celebrations of ${hostNames}, we kindly request you to upload a valid government-issued ID.`,
      'Your ID will be used only for hotel check-in formalities.',
      'Please upload your ID securely using the link below:',
      'Thank you for helping us make your stay comfortable.',
    ],
  },
  professional_photos: {
    heading: '📸 Wedding Photos Are Ready',
    linkAfter: 1,
    paragraphs: ({ guestName }) => [
      `Dear ${guestName},`,
      'The photographers have uploaded their clicks from the wedding. We invite you to access them and relive moments captured during the wedding using the link below:',
      'We hope you enjoy revisiting these wonderful memories. 💗',
    ],
  },
  utility_candid_photo: {
    heading: '📷 Share Your Candid Moments',
    paragraphs: ({ guestName, hostNames, link }) => [
      `Dear ${guestName},`,
      `We'd love to see the special moments you captured during the wedding celebrations of ${hostNames}.`,
      `To share your candid photos, open ${link} in your browser. This secure page will guide you through the upload.`,
      'Thank you for helping everyone relive the celebration through your eyes. 💗',
    ],
    signature: ({ hostNames, managerName }) => [
      `With warm wishes from ${hostNames}.`,
      `This experience is managed by ${managerName} for all guests.`,
    ],
  },
  baby_shower_invitation: {
    heading: '🍼 Baby Shower Invitation',
    paragraphs: ({ guestName, hostNames, eventDates, eventVenue, link }) => [
      `Dear ${guestName},`,
      `You are warmly invited to celebrate the upcoming arrival of ${hostNames}.`,
      `🗓️ Date: ${eventDates}\n📍 Venue: ${eventVenue}`,
      `To view more information and confirm your attendance, open ${link} in your browser. We look forward to hearing from you.`,
      'We look forward to celebrating this joyful occasion with you. 💛',
    ],
    signature: ({ hostNames, managerName }) => [
      `With warm wishes from ${hostNames}.`,
      `Event coordination is provided by ${managerName} with care.`,
    ],
  },
  birthday_invitation_simple: {
    heading: '🎂 Birthday Celebration',
    paragraphs: ({ guestName, hostNames, eventDates, eventVenue, link }) => [
      `Dear ${guestName},`,
      `You are warmly invited to celebrate the birthday of ${hostNames} with us.`,
      `🗓️ Date: ${eventDates}\n📍 Venue: ${eventVenue}`,
      `To view more information and confirm your attendance, open ${link} in your browser. We look forward to hearing from you.`,
      'We look forward to celebrating together. 🎉',
    ],
    signature: ({ hostNames, managerName }) => [
      `With warm wishes from ${hostNames}.`,
      `Event coordination is provided by ${managerName} with care.`,
    ],
  },
  corporate_invitation_simple: {
    heading: '📅 Event Invitation',
    paragraphs: ({ guestName, eventName, eventDates, eventVenue, link }) => [
      `Dear ${guestName},`,
      `You are invited to ${eventName}.`,
      `🗓️ Date: ${eventDates}\n📍 Venue: ${eventVenue}`,
      `For event information and to confirm your attendance, open ${link} in your browser. Registration details are available there.`,
      'We look forward to welcoming you.',
    ],
    signature: ({ hostNames, managerName }) => [
      `Hosted by ${hostNames}.`,
      `Event coordination is managed by ${managerName} for attendees.`,
    ],
  },
  accomodation_update_notifications: {
    heading: '🏨 Accommodation Details',
    linkAfter: 3,
    paragraphs: ({ guestName, hostNames, hotelName, roomNumber, checkInDate, checkOutDate }) => [
      `Dear ${guestName},`,
      `We're excited to host you at the wedding celebrations of ${hostNames}.`,
      `To make your visit comfortable, we have arranged your accommodation for the duration of your stay.\n\n🏨 Hotel: ${hotelName}\n🚪 Room Number: ${roomNumber}\n🏢 Check-in: ${checkInDate}\n🏢 Check-out: ${checkOutDate}`,
      'At your convenience, you can view your accommodation details here:',
      'We wish you a pleasant stay and look forward to celebrating with you 💗',
    ],
  },
  wedding_invitation_simple: {
    heading: '💍 Wedding Invitation',
    linkAfter: 3,
    paragraphs: ({ guestName, hostNames, eventDates, eventVenue }) => [
      `Dear ${guestName},`,
      `You are cordially invited to celebrate the wedding of ${hostNames}`,
      `🗓️ Dates: ${eventDates}\n📍 Venue: ${eventVenue}`,
      'Please use below link for more information and to confirm your RSVP:',
      'We look forward to celebrating this special occasion with you 💗',
    ],
  },
  wedding_invitation_image: {
    heading: '💍 Wedding Invitation',
    linkAfter: 3,
    mediaType: 'image',
    paragraphs: ({ guestName, hostNames, eventDates, eventVenue }) => [
      `Dear ${guestName},`,
      `You are cordially invited to celebrate the wedding of ${hostNames}`,
      `🗓️ Dates: ${eventDates}\n📍 Venue: ${eventVenue}`,
      'Please use below link for more information and to confirm your RSVP:',
      'We look forward to celebrating this special occasion with you 💗',
    ],
  },
  wedding_invitation_video: {
    heading: '💍 Wedding Invitation',
    linkAfter: 3,
    mediaType: 'video',
    paragraphs: ({ guestName, hostNames, eventDates, eventVenue }) => [
      `Dear ${guestName},`,
      `You are cordially invited to celebrate the wedding of ${hostNames}`,
      `🗓️ Dates: ${eventDates}\n📍 Venue: ${eventVenue}`,
      'Please use below link for more information and to confirm your RSVP:',
      'We look forward to celebrating this special occasion with you 💗',
    ],
  },
  wedding_invitation_utility: {
    heading: '💍 Wedding Invitation',
    linkAfter: 3,
    paragraphs: ({ guestName, hostNames, eventDates, eventVenue }) => [
      `Dear ${guestName},`,
      `You are cordially invited to celebrate the wedding of ${hostNames}`,
      `🗓️ Dates: ${eventDates}\n📍 Venue: ${eventVenue}`,
      'Please use below link for more information and to confirm your RSVP:',
      'We look forward to celebrating this special occasion with you 💗',
    ],
  },
  utility_rsvp_reminder: {
    heading: '💌 Looking forward to your confirmation',
    linkAfter: 2,
    paragraphs: ({ guestName, hostNames }) => [
      `Dear ${guestName},`,
      `We're excited to celebrate the wedding of ${hostNames}, and we'd love to have you join us.`,
      "If you haven't had a chance to respond yet, please confirm your attendance by using the link below:",
      'Your response will help us make the necessary arrangements for your comfort.',
      'Thank you, and we hope to celebrate with you! 💗',
    ],
  },
  photo_only: {
    heading: '',
    mediaType: 'image',
    showSignature: false,
    showCta: false,
    paragraphs: () => [],
  },
};

export function WhatsAppTemplatePreview({ templateName, variables = {}, onRemoveMedia, headerControls, onSecretMessageChange }) {
  const messageInputRef = useRef(null);
  useLayoutEffect(() => {
    const input = messageInputRef.current;
    if (!input) return undefined;
    const resize = () => {
      input.style.height = '0px';
      input.style.height = `${input.scrollHeight}px`;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [variables.secretMessage, templateName, onSecretMessageChange]);
  const secretTemplate = /^birthday_invitation_secret(?:_(image|video))?$/.exec(templateName || '');
  const mediaTemplateMatch = String(templateName || '').match(/^(wedding|baby_shower|birthday|corporate|event)_invitation_(image|video)$/);
  const mediaTemplateBase = mediaTemplateMatch ? ({
    wedding: 'wedding_invitation_utility',
    baby_shower: 'baby_shower_invitation',
    birthday: 'birthday_invitation_simple',
    corporate: 'corporate_invitation_simple',
    event: 'corporate_invitation_simple',
  }[mediaTemplateMatch[1]]) : null;
  const baseTemplate = mediaTemplateBase ? TEMPLATE_COPY[mediaTemplateBase] : null;
  const omitDateAndVenue = mediaTemplateMatch && ['baby_shower', 'birthday'].includes(mediaTemplateMatch[1]);
  const template = secretTemplate ? {
    heading: '🥂✨ Shhhh… it’s a SURPRISE! 🤫 ✨🥂',
    mediaType: secretTemplate[1],
    showSignature: false,
    showCta: false,
    paragraphs: () => [variables.secretMessage || '', `Looking forward to celebrating ${variables.celebrantName || variables.hostNames || '{{celebrant}}'}’s fabulous ${variables.birthdayMilestone || '{{years}}th Birthday'}! 🥂💙✨`],
  } : TEMPLATE_COPY[templateName] || (baseTemplate ? {
    ...baseTemplate,
    mediaType: mediaTemplateMatch[2],
    paragraphs: omitDateAndVenue
      ? (values) => baseTemplate.paragraphs(values).filter((paragraph) => !paragraph.includes('Date:') && !paragraph.includes('Venue:'))
      : baseTemplate.paragraphs,
  } : null);
  if (!template) {
    return (
      <div className="rounded-xl border border-dashed bg-muted/30 px-5 py-10 text-center">
        <p className="text-sm font-medium">Template preview is not available yet</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">The approved WhatsApp template will still be used when this message is sent.</p>
      </div>
    );
  }

  const values = {
    guestName: variables.guestName || 'Guest',
    hostNames: variables.hostNames || 'the couple',
    eventName: variables.eventName || 'the event',
    eventDates: variables.eventDates || 'Event dates',
    eventVenue: variables.eventVenue || 'Event venue',
    hotelName: variables.hotelName || 'Hotel',
    roomNumber: variables.roomNumber || 'To be assigned',
    checkInDate: variables.checkInDate || 'Check-in date',
    checkOutDate: variables.checkOutDate || 'Check-out date',
    link: variables.link || 'https://galago.in/invite',
  };
  const paragraphs = template.paragraphs(values);
  const managerName = variables.managerName || 'Your Event Team';
  const signatureLines = template.signature
    ? template.signature({ ...values, managerName })
    : [values.hostNames, `Event Managed by ${managerName}`, 'Powered by GalaGo'];

  return (
    <div className="overflow-hidden rounded-xl border bg-[#efeae2] shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b bg-white px-4 py-3 text-sm font-semibold text-slate-800">
        <span className="shrink-0">Your template</span>
        {headerControls}
      </div>
      <div
        className="p-4 sm:p-5"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,.42) 0 1px, transparent 1.5px)', backgroundSize: '16px 16px' }}
      >
        <div className="mx-auto max-w-sm overflow-hidden rounded-lg bg-white shadow-md">
          {template.mediaType && (
            variables.mediaUrl ? (
              <div className="relative">
                {template.mediaType === 'video' ? (
                  <video className="aspect-video w-full bg-slate-100 object-cover" controls src={variables.mediaUrl}>
                    <track kind="captions" />
                  </video>
                ) : <img className="aspect-video w-full bg-slate-100 object-cover" src={variables.mediaUrl} alt="Invitation media preview" />}
                {onRemoveMedia && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="absolute right-2 top-2 h-8 bg-white/95 px-2.5 text-xs shadow hover:bg-white"
                    onClick={onRemoveMedia}
                  >
                    <X className="mr-1 h-3.5 w-3.5" />Remove media
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid aspect-video place-items-center bg-gradient-to-br from-amber-50 to-rose-100 px-6 text-center text-sm font-medium text-slate-500">
                Invitation media preview
              </div>
            )
          )}
          <div className="space-y-4 px-4 pb-3 pt-4 text-[13px] leading-[1.45] text-slate-800">
            {template.heading && <p className="font-semibold">{template.heading}</p>}
            {paragraphs.map((paragraph, index) => (
              <React.Fragment key={`${templateName}-${index}`}>
                {secretTemplate && index === 0 && onSecretMessageChange ? (
                  <textarea
                    ref={messageInputRef}
                    aria-label="Invitation message"
                    value={variables.secretMessage || ''}
                    onChange={(event) => onSecretMessageChange(event.target.value)}
                    rows={1}
                    className="block w-full resize-none overflow-hidden rounded border border-dashed border-slate-300 bg-transparent p-1 text-inherit focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                ) : <p className="whitespace-pre-line">{paragraph}</p>}
                {template.linkAfter === index && <p className="-mt-2 break-all text-slate-700">{variables.webInviteLink || 'galago.in'}</p>}
              </React.Fragment>
            ))}
            {template.showSignature !== false && <div>
              {signatureLines.map((line, index) => (
                <p key={`${templateName}-signature-${index}`} className={index === signatureLines.length - 1 && !template.signature ? 'mt-1 text-xs text-slate-400' : undefined}>{line}</p>
              ))}
            </div>}
          </div>
          {template.showCta !== false && <div className="border-t px-4 py-3 text-center text-sm font-medium text-emerald-600">
            ↗ Visit website
          </div>}
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppSendConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  title = 'Send WhatsApp message?',
  description = 'This will send a WhatsApp message to the selected recipients.',
  confirmLabel = 'Send message',
  templateName,
  templateVariables,
  startOnPreview = false,
  disabled = false,
  testId = 'confirm-whatsapp-send-btn',
}) {
  const [showPreview, setShowPreview] = useState(false);
  const hasTemplatePreview = Boolean(TEMPLATE_COPY[templateName]);

  useEffect(() => {
    setShowPreview(Boolean(open && startOnPreview && hasTemplatePreview));
  }, [hasTemplatePreview, open, startOnPreview]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={showPreview ? 'max-w-lg' : undefined}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <WhatsappLogo className="h-5 w-5 text-green-600" weight="fill" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {showPreview && (
          <div className="space-y-2">
            <p className="text-sm font-semibold">Message preview</p>
            <p className="text-xs text-muted-foreground">Names and the secure link are personalized for each recipient.</p>
            <WhatsAppTemplatePreview templateName={templateName} variables={templateVariables} />
          </div>
        )}
        <AlertDialogFooter>
          {showPreview ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => (startOnPreview ? onOpenChange(false) : setShowPreview(false))}
              disabled={disabled}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />Back
            </Button>
          ) : <AlertDialogCancel disabled={disabled}>Cancel</AlertDialogCancel>}
          {hasTemplatePreview && !showPreview ? (
            <Button type="button" onClick={() => setShowPreview(true)} disabled={disabled} data-testid={`${testId}-next`}>
              Next<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onConfirm}
              disabled={disabled}
              className="bg-green-600 text-white hover:bg-green-700"
              data-testid={testId}
            >
              <WhatsappLogo className="mr-2 h-4 w-4" weight="fill" />
              {confirmLabel}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
